<template>
  <div class="book-food-page">
    <a-config-provider :locale="zhCN">
      <header class="page-header">
        <button class="back-button" aria-label="返回" @click="router.back()">
          <i class="fas fa-arrow-left"></i>
        </button>
        <h1>{{ pageTitle }}</h1>
      </header>
      <a-card :bordered="false">
        <a-form
          :model="filters"
          layout="inline"
          class="search-form flex flex-wrap"
          @finish="handleSearch"
        >
          <a-form-item label="餐厅" required>
            <a-select
              v-model:value="filters.shopId"
              placeholder="请选择餐厅"
              disabled
              class="filter-input"
              @change="cacheSelectedShop"
            >
              <a-select-option v-for="shop in shops" :key="shop.shopId" :value="shop.shopId">{{
                shop.shopName
              }}</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item :label="dateLabel" required>
            <a-range-picker v-model:value="dateRange" :allow-clear="false" class="date-input" />
          </a-form-item>
          <a-form-item label="菜品"
            ><a-input
              v-model:value="filters.shopFoodName"
              placeholder="输入菜品名称"
              allow-clear
              class="filter-input"
          /></a-form-item>
          <a-form-item label="分类"
            ><a-input
              v-model:value="filters.categoryName"
              placeholder="输入分类名称"
              allow-clear
              class="filter-input"
          /></a-form-item>
          <a-form-item label="订单编号"
            ><a-input
              v-model:value="filters.orderNo"
              placeholder="输入订单编号"
              allow-clear
              class="filter-input"
          /></a-form-item>
          <a-form-item label="订单状态">
            <a-select
              v-model:value="filters.orderStatuss"
              mode="multiple"
              :max-tag-count="1"
              :options="orderStatusOptions"
              placeholder="全部状态"
              allow-clear
              class="filter-input"
            />
          </a-form-item>
          <a-form-item label="支付状态">
            <a-select
              v-model:value="filters.paymentStatuss"
              mode="multiple"
              :max-tag-count="1"
              :options="paymentStatusOptions"
              placeholder="全部状态"
              allow-clear
              class="filter-input"
            />
          </a-form-item>
          <a-form-item>
            <div class="flex flex-wrap gap-2">
              <van-button type="primary" size="small" native-type="submit" :loading="loading"
                >查询</van-button
              >
              <van-button size="small" :disabled="loading" @click="handleReset">重置</van-button>
              <van-button
                type="success"
                size="small"
                :disabled="loading || !rows.length"
                :loading="exportLoading"
                @click="handleExport"
                >导出 Excel</van-button
              >
            </div>
          </a-form-item>
        </a-form>
        <a-alert v-if="error" :message="error" type="error" show-icon class="mb-4" />
        <a-spin :spinning="loading" :tip="`正在加载明细 ${loadedCount} / ${totalCount} 条`">
          <div class="result-caption">{{ resultCaption }}</div>
          <div class="stats-grid">
            <div v-for="stat in stats" :key="stat.label" class="stat-card"
              ><span>{{ stat.label }}</span
              ><strong>{{ stat.value }}</strong></div
            >
          </div>
          <a-tabs v-model:active-key="activeTab">
            <a-tab-pane key="summary" tab="每日菜品汇总">
              <div class="summary-note"
                >按{{ dateLabel }}、餐厅、分类、菜品汇总；有效数量和金额不含已取消订单及菜品。</div
              >
              <a-table
                :columns="summaryColumns"
                :data-source="summaryRows"
                row-key="key"
                :pagination="summaryPagination"
                :scroll="{ x: 1000 }"
                size="middle"
              />
            </a-tab-pane>
            <a-tab-pane key="detail" :tab="`订单明细（${rows.length}）`">
              <a-table
                :columns="detailColumns"
                :data-source="detailRows"
                row-key="key"
                :pagination="detailPagination"
                :scroll="{ x: 2000 }"
                size="middle"
              />
            </a-tab-pane>
          </a-tabs>
        </a-spin>
      </a-card>
    </a-config-provider>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { showToast } from 'vant';
  import type { TableColumnsType } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { Dayjs } from 'dayjs';
  import * as XLSX from 'xlsx';
  import { restoreSelectedShop, cacheSelectedShop } from '@/utils/selectedShop';
  import { fetchShopList } from '@/service/base';
  import { getBookOrderFood, getBookOrderFoodSum } from '@/service/bookOrderFood';
  import { getDeliveryOrderFood, getDeliveryOrderFoodSum } from '@/service/deliveryOrderFood';
  import {
    BookFoodShopModel,
    BookOrderFoodFilterModel,
    BookOrderFoodModel,
    BookOrderFoodSumModel,
  } from '@/model/bookOrderFood';
  import { aggregateBookOrderFood } from '@/utils/bookOrderFood';

  const props = withDefaults(defineProps<{ reportType?: 'booking' | 'delivery' }>(), {
    reportType: 'booking',
  });
  const router = useRouter();
  const isDelivery = computed(() => props.reportType === 'delivery');
  const pageTitle = computed(() => (isDelivery.value ? '外卖菜品' : '预定菜品'));
  const dateLabel = computed(() => (isDelivery.value ? '外卖日期' : '预定日期'));
  const shops = ref<BookFoodShopModel[]>([]);
  const dateRange = ref<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().add(2, 'day').endOf('day'),
  ]);
  const filters = reactive<Omit<BookOrderFoodFilterModel, 'selfPickDateStart' | 'selfPickDateEnd'>>(
    {},
  );
  const rows = ref<BookOrderFoodModel[]>([]);
  const reportSummary = ref<BookOrderFoodSumModel | null>(null);
  const loading = ref(false);
  const exportLoading = ref(false);
  const error = ref('');
  const loadedCount = ref(0);
  const totalCount = ref(0);
  const activeTab = ref('summary');
  const resultCaption = ref('');
  const resultRange = ref('');
  let requestId = 0;
  let disposed = false;
  const orderStatusOptions = [
    { label: '已点餐', value: 1 },
    { label: '待出餐', value: 2 },
    { label: '已出餐', value: 8 },
    { label: '已完成', value: 10 },
    { label: '已取消', value: 999 },
  ];
  const paymentStatusOptions = [
    { label: '待支付', value: 0 },
    { label: '部分支付', value: 5 },
    { label: '已支付', value: 10 },
  ];
  const summaryPagination = reactive({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    onChange: (page: number, size: number) => {
      summaryPagination.current = page;
      summaryPagination.pageSize = size;
    },
  });
  const detailPagination = reactive({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    onChange: (page: number, size: number) => {
      detailPagination.current = page;
      detailPagination.pageSize = size;
    },
  });
  const summaryRows = computed(() => aggregateBookOrderFood(rows.value));
  const detailRows = computed(() => rows.value.map((row, index) => ({ ...row, key: index })));
  const formatNumber = (value: number): string =>
    value.toLocaleString('zh-CN', { maximumFractionDigits: 6 });
  const stats = computed(() => {
    const summary = reportSummary.value;
    return [
      {
        label: isDelivery.value ? '外卖天数' : '预定天数',
        value: new Set(summaryRows.value.map((row) => row.date)).size,
      },
      {
        label: '有效订单',
        value: summary ? summary.totalOrderCount - summary.cancelOrderCount : 0,
      },
      {
        label: '有效菜品数量',
        value: formatNumber(summary?.totalFoodQuantity ?? 0),
      },
      {
        label: '取消数量',
        value: formatNumber(summary?.cancelFoodQuantity ?? 0),
      },
      {
        label: '有效金额',
        value: `¥${(summary?.totalAmount ?? 0).toFixed(2)}`,
      },
    ];
  });
  const summaryFields = computed(() => [
    [dateLabel.value, 'date'],
    ['餐厅', 'shopName'],
    ['菜品分类', 'categoryName'],
    ['菜品名称', 'shopFoodName'],
    ['有效订单数', 'orderCount'],
    ['有效数量', 'quantity'],
    ['取消数量', 'cancelledQuantity'],
    ['有效金额', 'amount'],
  ]);
  const detailFields = computed(() => [
    [dateLabel.value, 'selfPickDate'],
    [isDelivery.value ? '外卖时间' : '预定时间', 'selfPickTime'],
    ...(isDelivery.value ? [['创建时间', 'createTime']] : []),
    ['订单编号', 'orderNo'],
    ['餐厅', 'shopName'],
    ['桌台', 'tableName'],
    ['菜品分类', 'categoryName'],
    ['菜品名称', 'shopFoodName'],
    ['规格', 'foodSpecName'],
    ['数量', 'quantity'],
    ['单价', 'price'],
    ['金额', 'amount'],
    ['订单状态', 'orderStatusName'],
    ['菜品状态', 'foodStatusName'],
    ['支付状态', 'paymentStatusName'],
    ['支付方式', 'paymentMethodName'],
    ...(!isDelivery.value ? [['创建时间', 'createTime']] : []),
  ]);
  const makeColumns = (fields: string[][]): TableColumnsType =>
    fields.map(([title, dataIndex]) => ({
      title,
      dataIndex,
      key: dataIndex,
      width: dataIndex === 'orderNo' || dataIndex === 'createTime' ? 180 : 125,
      customRender: ({ text }: { text: unknown }) => {
        if (['price', 'amount'].includes(dataIndex)) return `¥${Number(text || 0).toFixed(2)}`;
        if (dataIndex === 'createTime')
          return text ? dayjs(String(text)).format('YYYY-MM-DD HH:mm:ss') : '—';
        return text === undefined || text === null || text === '' ? '—' : String(text);
      },
    }));
  const summaryColumns = computed(() => makeColumns(summaryFields.value));
  const detailColumns = computed(() => makeColumns(detailFields.value));
  const setRange = (days: number): void => {
    dateRange.value = [
      dayjs().startOf('day'),
      dayjs()
        .add(days - 1, 'day')
        .endOf('day'),
    ];
  };
  const handleSearch = async (): Promise<void> => {
    if (loading.value) return;
    if (!filters.shopId) {
      rows.value = [];
      resultCaption.value = '';
      showToast('请选择餐厅');
      return;
    }
    if (
      !dateRange.value?.[0]?.isValid() ||
      !dateRange.value?.[1]?.isValid() ||
      dateRange.value[0].isAfter(dateRange.value[1])
    ) {
      showToast(`请选择有效的${dateLabel.value}范围`);
      return;
    }
    const id = ++requestId;
    const delivery = isDelivery.value;
    const data: BookOrderFoodFilterModel = {
      ...filters,
      orderNo: filters.orderNo?.trim() || undefined,
      shopFoodName: filters.shopFoodName?.trim() || undefined,
      categoryName: filters.categoryName?.trim() || undefined,
      orderStatuss: filters.orderStatuss?.length ? [...filters.orderStatuss] : undefined,
      paymentStatuss: filters.paymentStatuss?.length ? [...filters.paymentStatuss] : undefined,
      selfPickDateStart: dateRange.value[0].format('YYYY-MM-DD'),
      selfPickDateEnd: dateRange.value[1].format('YYYY-MM-DD'),
    };
    loading.value = true;
    error.value = '';
    rows.value = [];
    reportSummary.value = null;
    resultCaption.value = '';
    loadedCount.value = 0;
    totalCount.value = 0;
    try {
      const allRows: BookOrderFoodModel[] = [];
      let pageNum = 1;
      let expectedTotal: number | undefined;
      do {
        const response = delivery
          ? await getDeliveryOrderFood({ pageNum, pageSize: 500, data })
          : await getBookOrderFood({ pageNum, pageSize: 500, data });
        if (id !== requestId) return;
        if (response.code !== 200) throw new Error(response.msg || '查询失败');
        const result = response.result;
        if (!result || !Array.isArray(result.list) || !Number.isFinite(Number(result.total)))
          throw new Error('接口返回的数据格式异常');
        const total = Number(result.total);
        if (expectedTotal !== undefined && expectedTotal !== total)
          throw new Error('查询期间数据发生变化，请重新查询');
        expectedTotal = total;
        if (!result.list.length && allRows.length < total)
          throw new Error('明细未加载完整，请重新查询');
        allRows.push(...result.list);
        totalCount.value = total;
        loadedCount.value = allRows.length;
        pageNum += 1;
      } while (allRows.length < expectedTotal);
      const summaryResponse = delivery
        ? await getDeliveryOrderFoodSum({ pageNum: 1, pageSize: 500, data })
        : await getBookOrderFoodSum({ pageNum: 1, pageSize: 500, data });
      if (id !== requestId) return;
      if (summaryResponse.code !== 200 || !summaryResponse.result) {
        throw new Error(summaryResponse.msg || '查询汇总数据失败');
      }
      reportSummary.value = summaryResponse.result;
      rows.value = allRows;
      summaryPagination.current = 1;
      detailPagination.current = 1;
      resultRange.value = `${data.selfPickDateStart}_${data.selfPickDateEnd}`;
      const shopName =
        shops.value.find((shop) => shop.shopId === data.shopId)?.shopName || '未知餐厅';
      resultCaption.value = `${shopName} · ${dateLabel.value} ${data.selfPickDateStart} 至 ${data.selfPickDateEnd} · 共 ${allRows.length} 条明细`;
      cacheSelectedShop(data.shopId);
    } catch (cause) {
      if (id !== requestId) return;
      console.error(`查询${pageTitle.value}失败:`, cause);
      error.value =
        cause instanceof Error ? cause.message : `查询${pageTitle.value}失败，请稍后重试`;
    } finally {
      if (id === requestId) loading.value = false;
    }
  };
  const handleReset = (): void => {
    Object.assign(filters, {
      orderNo: undefined,
      shopFoodName: undefined,
      categoryName: undefined,
      orderStatuss: undefined,
      paymentStatuss: undefined,
    });
    setRange(3);
    void handleSearch();
  };
  const handleExport = (): void => {
    if (loading.value || !rows.value.length) return;
    exportLoading.value = true;
    try {
      const workbook = XLSX.utils.book_new();
      const appendSheet = (name: string, fields: string[][], data: object[]): void => {
        const sheet = XLSX.utils.aoa_to_sheet([
          fields.map(([title]) => title),
          ...data.map((row) => {
            const values = new Map(Object.entries(row));
            return fields.map(([, key]) => values.get(key) ?? '');
          }),
        ]);
        sheet['!cols'] = fields.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(workbook, sheet, name);
      };
      appendSheet('每日菜品汇总', summaryFields.value, summaryRows.value);
      appendSheet('订单明细', detailFields.value, rows.value);
      const notes = XLSX.utils.aoa_to_sheet([
        ['查询范围', resultCaption.value],
        [
          '汇总口径',
          `按${dateLabel.value}、餐厅、分类、菜品汇总；有效数量、订单数及金额不含已取消订单和菜品。`,
        ],
        ['说明', '导出内容为最近一次成功查询的全部结果，明细包含取消项。'],
      ]);
      XLSX.utils.book_append_sheet(workbook, notes, '说明');
      XLSX.writeFile(workbook, `${pageTitle.value}_${resultRange.value}.xlsx`);
      showToast('导出成功');
    } catch (cause) {
      console.error(`导出${pageTitle.value}失败:`, cause);
      showToast('导出失败，请重试');
    } finally {
      exportLoading.value = false;
    }
  };
  watch(
    () => props.reportType,
    () => {
      requestId += 1;
      rows.value = [];
      reportSummary.value = null;
      error.value = '';
      resultCaption.value = '';
      resultRange.value = '';
      loadedCount.value = 0;
      totalCount.value = 0;
      loading.value = false;
      activeTab.value = 'summary';
      summaryPagination.current = 1;
      detailPagination.current = 1;
      Object.assign(filters, {
        orderNo: undefined,
        shopFoodName: undefined,
        categoryName: undefined,
        orderStatuss: undefined,
        paymentStatuss: undefined,
      });
      setRange(3);
      if (filters.shopId) void handleSearch();
    },
  );
  onMounted(async () => {
    try {
      const response = await fetchShopList({ pageNum: 1, pageSize: 999, data: { shopStatus: 0 } });
      if (disposed) return;
      if (response.code !== 200) throw new Error(response.msg || '加载餐厅失败');
      shops.value = response.result?.list || [];
      filters.shopId = restoreSelectedShop(shops.value);
    } catch (cause) {
      if (disposed) return;
      console.error('加载餐厅失败:', cause);
      showToast('加载餐厅失败，请刷新重试');
    }
    if (!disposed && filters.shopId) void handleSearch();
  });
  onBeforeUnmount(() => {
    disposed = true;
    requestId += 1;
  });
</script>

<style scoped lang="less">
  .book-food-page {
    background: #f5f7fa;
    min-height: 100vh;
    :deep(.ant-card-body) {
      padding: 12px 16px;
    }
    .page-header {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #3b82f6;
      padding: 6px 12px;
      color: white;
    }
    .back-button {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      border: 0;
      border-radius: 8px;
      background: #2563eb;
      color: white;
      cursor: pointer;
    }
    h1 {
      color: white;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
    .search-form :deep(.ant-form-item) {
      margin-bottom: 16px;
    }
    .filter-input {
      width: 180px;
    }
    .date-input {
      width: 270px;
    }
    .result-caption {
      color: #64748b;
      font-size: 13px;
      margin: 8px 0 16px;
    }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 12px;
    }
    .stat-card {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
    }
    .stat-card span {
      color: #64748b;
      font-size: 13px;
    }
    .stat-card strong {
      color: #1d4ed8;
      font-size: 24px;
      overflow-wrap: anywhere;
    }
    .summary-note {
      background: #f0f5ff;
      color: #334155;
      padding: 12px 16px;
      border-radius: 6px;
      margin-bottom: 16px;
    }
    @media (max-width: 768px) {
      :deep(.ant-card-body) {
        padding: 12px;
      }
      .stats-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .stat-card:last-child {
        grid-column: 1 / -1;
      }
      .search-form :deep(.ant-form-item) {
        width: 100%;
        margin-right: 0;
      }
      .filter-input,
      .date-input {
        width: 100%;
      }
    }
  }
</style>
